import {
  ApplicationRef,
  Component,
  computed,
  ElementRef,
  EventEmitter,
  inject,
  input,
  Input,
  Output,
  ViewChild
} from "@angular/core";
import { marker } from "@jsverse/transloco-keys-manager/marker";
import { DxHtmlEditorComponent } from "devextreme-angular";
import { ArrayStore, CustomStore, DataSource } from "devextreme/common/data";
import {
  ContentReadyEvent,
  dxHtmlEditorToolbar,
  HtmlEditorPredefinedToolbarItem
} from "devextreme/ui/html_editor";
import { lastValueFrom } from "rxjs";
import { ReminderTemplateBackend } from "src/app/backend/api/reminder-template.backend";
import { CampaignTemplate } from "src/app/backend/models/campaign-template";
import { MessageTemplateType } from "src/app/backend/models/message-template";
import { sntConfirm } from "src/app/common/dialog";
import { TinyEditorDirective } from "src/app/components/ui/wysiwyg-editor/tiny-editor.directive";
import { blockWysiwygTemplateMount } from "src/app/modules/bootstrap/pms-ui/blocks/wysiwyg-block/wysiwyg-template.block";
import { RegionService } from "src/app/services/feature-access-control/region.service";
import { blobToDataUrl } from "src/app/support/blob";
import { sntTranslate } from "src/app/support/translate";
import { EditorOptions as TinyEditorOptions } from "tinymce";

export enum WysiwygEngine {
  DEVX = "devx",
  TINY = "tiny"
}

type WysiwygToolbarType =
  | "small"
  | "signature"
  | "template-editor"
  | "default"
  | false;

@Component({
  standalone: false,
  selector: "snt-wysiwyg-editor",
  templateUrl: "wysiwyg-editor.component.html",
  styleUrl: "wysiwyg-editor.component.scss"
})
export class WysiwygEditorComponent {
  @ViewChild(TinyEditorDirective)
  tinyEditorDirective: TinyEditorDirective | undefined;

  @ViewChild(DxHtmlEditorComponent)
  devxHtmlEditorComponent: DxHtmlEditorComponent | undefined;

  @ViewChild("target")
  targetRef: ElementRef<HTMLElement> | undefined;

  @Input()
  initialContent: string | undefined;

  @Input()
  mergeTagsList: any | undefined;

  @Input()
  containerMode: "detached" | "inside" = "detached";

  @Input()
  maxImageSize?: number;

  @Input()
  engine: WysiwygEngine = WysiwygEngine.TINY;

  @Input()
  placeholder: string = "";

  @Input()
  readOnly: boolean = false;

  enableTemplates = input<boolean>(false);

  toolbar = input<WysiwygToolbarType>("default");
  devxEngineMinimal = input<boolean>(false);

  variablesDataSource = input<string[] | undefined>(undefined);
  variablesEscapeChar = input<string[] | undefined>(undefined);
  variablesEnabled = computed<boolean>(() => {
    return !!this.variablesDataSource() && !!this.variablesEscapeChar();
  });

  @Output()
  contentChanged: EventEmitter<void> = new EventEmitter<void>();

  tinyEditorOptions = computed<Partial<TinyEditorOptions> | undefined>(() => {
    const toolbar = this.toolbar();

    const tinyEditorOptions: Partial<TinyEditorOptions> = {
      setup: (editor) => {
        editor.on("init", () => {
          if (this.initialContent) {
            editor.setContent(this.initialContent, {
              format: "html",
              initial: true,
              no_events: true
            });
          }
        });
      },
      menubar: false,
      plugins:
        "preview casechange importcss tinydrive searchreplace autolink save directionality advcode visualblocks visualchars fullscreen image link media mediaembed template codesample table charmap pagebreak nonbreaking anchor tableofcontents insertdatetime advlist lists checklist wordcount editimage help formatpainter permanentpen pageembed charmap quickbars emoticons advtable export mergetags".split(
          " "
        ),
      toolbar:
        "undo redo | bold italic underline strikethrough | fontfamily fontsize blocks | alignleft aligncenter alignright alignjustify | outdent indent |  numlist bullist checklist | forecolor backcolor casechange permanentpen formatpainter removeformat | pagebreak | charmap emoticons | fullscreen  preview | pageembed template link anchor | mergetags | spellchecker",
      toolbar_mode: "wrap",
      spellchecker_language: "it",
      mergetags_prefix: "{*",
      mergetags_suffix: "}",
      mergetags_list: this.mergeTagsList,
      height: "100%",
      language: this.regionService.get(),
      content_langs: [{ title: "Italiano", code: "it" }],
      resize: false,
      font_size_formats: "6pt 7pt 8pt 10pt 12pt 14pt 16pt 18pt 24pt 36pt 48pt",
      images_upload_handler: async (blobInfo, progress) => {
        return blobToDataUrl(blobInfo.blob());
      }
    };

    switch (toolbar) {
      case "small": {
        tinyEditorOptions.toolbar =
          "bold italic underline strikethrough | fontfamily fontsize blocks | alignleft aligncenter alignright alignjustify | forecolor backcolor formatpainter removeformat";
        break;
      }
      case "signature": {
        tinyEditorOptions.toolbar =
          "bold italic underline strikethrough | fontfamily fontsize | alignleft aligncenter alignright | bullist | forecolor backcolor | emoticons";
        break;
      }
      case "template-editor": {
        tinyEditorOptions.toolbar =
          "bold italic underline strikethrough | fontfamily fontsize blocks | alignleft aligncenter alignright alignjustify | outdent indent |  numlist bullist checklist | removeformat | mergetags signature";
        break;
      }
      case "default": {
        tinyEditorOptions.toolbar =
          "bold italic underline strikethrough | fontfamily fontsize blocks | alignleft aligncenter alignright alignjustify | forecolor backcolor formatpainter removeformat";
        break;
      }
      case false: {
        tinyEditorOptions.toolbar = false;
        break;
      }
    }

    return tinyEditorOptions;
  });

  devxEditorToolbar = computed<dxHtmlEditorToolbar>(() => {
    const toolbar = this.toolbar();
    const devxEngineMinimal = this.devxEngineMinimal();
    const enableTemplates = this.enableTemplates();
    const variablesEnabled = this.variablesEnabled();

    const dxToolbar: dxHtmlEditorToolbar = {
      items: [
        {
          name: "size",
          acceptedValues: [
            "8pt",
            "10pt",
            "12pt",
            "14pt",
            "18pt",
            "24pt",
            "36pt"
          ]
        },
        {
          name: "font",
          acceptedValues: [
            "Arial",
            "Courier New",
            "Georgia",
            "Impact",
            "Lucida Console",
            "Tahoma",
            "Times New Roman",
            "Verdana"
          ]
        },
        ...(<HtmlEditorPredefinedToolbarItem[]>[
          "separator",
          "bold",
          "italic",
          "strike",
          "underline",
          "separator",
          "alignLeft",
          "alignCenter",
          "alignRight",
          "alignJustify",
          "separator",
          "orderedList",
          "bulletList",
          "separator",
          "insertTable",
          "deleteTable",
          "insertRowAbove",
          "insertRowBelow",
          "deleteRow",
          "insertColumnLeft",
          "insertColumnRight",
          "deleteColumn"
        ])
      ].filter((x: any) => {
        if (devxEngineMinimal && x.name === "font") {
          return false;
        }

        return true;
      })
    };

    if (variablesEnabled) {
      dxToolbar.items?.splice(0, 0, {
        widget: "dxDropDownButton",
        options: {
          text: sntTranslate(marker("Variabili")),
          icon: "fa-light fa-hashtag",
          displayExpr: "name",
          valueExpr: "id",
          focusStateEnabled: false,
          dataSource: new DataSource({
            paginate: false,
            store: new ArrayStore({
              data:
                this.variablesDataSource()?.map((x: string) => {
                  return {
                    id: x,
                    name: sntTranslate(`variable-${x}`)
                  };
                }) || []
            })
          }),
          itemTemplate: (
            data: { id: string; name: string },
            index: number,
            element: HTMLElement
          ) => {
            element.innerHTML = data.name;
          },
          dropDownOptions: {
            width: 220
          },
          onSelectionChanged: ({
            item
          }: {
            item: { id: string; name: string };
          }) => {
            const quill =
              this.devxHtmlEditorComponent?.instance.getQuillInstance();

            const selection = quill.getSelection(true);
            const caretPosition = selection
              ? selection.index
              : quill.getLength();

            const variableData = {
              value: item.id,
              escapeChar: this.variablesEscapeChar() || ["{{", "}}"]
            };

            // HACK setTimeout necessario per non perdere il focus dell'edito
            // non so il motivo, visto nel codice di DevExtreme
            setTimeout(() => {
              quill.insertEmbed(caretPosition, "variable", variableData);
              quill.setSelection(caretPosition + 1);
            }, 0);
          }
        }
      });
    }

    if (enableTemplates) {
      dxToolbar.items?.splice(0, 0, {
        widget: "dxDropDownButton",
        options: {
          text: sntTranslate(marker("Template")),
          icon: "fa-light fa-envelope-open-text",
          displayExpr: "name",
          valueExpr: "id",
          focusStateEnabled: false,
          dataSource: new DataSource({
            paginate: false,
            store: new CustomStore({
              load: async () => {
                return await lastValueFrom(
                  this.reminderTemplateBackend.listAllByType(
                    MessageTemplateType.CAMPAIGN
                  )
                );
              }
            })
          }),
          itemTemplate: (
            data: CampaignTemplate,
            index: number,
            element: HTMLElement
          ) => {
            blockWysiwygTemplateMount({
              element,
              applicationRefInjector: this.applicationRef.injector,
              template: data
            });
          },
          dropDownOptions: {
            width: 220
          },
          onSelectionChanged: ({ item }: { item: CampaignTemplate }) => {
            this.setContent(item.content);
          }
        }
      });
    }

    return dxToolbar;
  });

  private readonly regionService = inject(RegionService);
  private readonly reminderTemplateBackend = inject(ReminderTemplateBackend);
  private readonly applicationRef = inject(ApplicationRef);

  activated(): void {
    this.tinyEditorDirective?.activated();
  }

  deactivated(): void {
    this.tinyEditorDirective?.decativated();
  }

  destroied(): void {
    this.tinyEditorDirective?.destroied();
  }

  getContent(skipToxStyle?: boolean): string | undefined {
    switch (this.engine) {
      case WysiwygEngine.DEVX: {
        return this.devxHtmlEditorComponent?.value || "";
      }
      case WysiwygEngine.TINY: {
        const editorContent = this.tinyEditorDirective?.getContent() || "";

        if (skipToxStyle) {
          return editorContent;
        }

        return `
      <style>
        .tox-checklist > li:not(.tox-checklist--hidden) {
          list-style: none;
          margin: 0.25em 0;
          position: relative;
        }
        
        .tox-checklist > li:not(.tox-checklist--hidden)::before {
          content: url("data:image/svg+xml;charset=UTF-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2216%22%20height%3D%2216%22%20viewBox%3D%220%200%2016%2016%22%3E%3Cg%20id%3D%22checklist-unchecked%22%20fill%3D%22none%22%20fill-rule%3D%22evenodd%22%3E%3Crect%20id%3D%22Rectangle%22%20width%3D%2215%22%20height%3D%2215%22%20x%3D%22.5%22%20y%3D%22.5%22%20fill-rule%3D%22nonzero%22%20stroke%3D%22%234C4C4C%22%20rx%3D%222%22%2F%3E%3C%2Fg%3E%3C%2Fsvg%3E%0A");
          cursor: pointer;
          height: 1em;
          margin-left: -1.5em;
          margin-top: 0.125em;
          position: absolute;
          width: 1em;
        }
        
        .tox-checklist li:not(.tox-checklist--hidden).tox-checklist--checked::before {
           content: url("data:image/svg+xml;charset=UTF-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2216%22%20height%3D%2216%22%20viewBox%3D%220%200%2016%2016%22%3E%3Cg%20id%3D%22checklist-checked%22%20fill%3D%22none%22%20fill-rule%3D%22evenodd%22%3E%3Crect%20id%3D%22Rectangle%22%20width%3D%2216%22%20height%3D%2216%22%20fill%3D%22%234099FF%22%20fill-rule%3D%22nonzero%22%20rx%3D%222%22%2F%3E%3Cpath%20id%3D%22Path%22%20fill%3D%22%23FFF%22%20fill-rule%3D%22nonzero%22%20d%3D%22M11.5703186%2C3.14417309%20C11.8516238%2C2.73724603%2012.4164781%2C2.62829933%2012.83558%2C2.89774797%20C13.260121%2C3.17069355%2013.3759736%2C3.72932262%2013.0909105%2C4.14168582%20L7.7580587%2C11.8560195%20C7.43776896%2C12.3193404%206.76483983%2C12.3852142%206.35607322%2C11.9948725%20L3.02491697%2C8.8138662%20C2.66090143%2C8.46625845%202.65798871%2C7.89594698%203.01850234%2C7.54483354%20C3.373942%2C7.19866177%203.94940006%2C7.19592841%204.30829608%2C7.5386474%20L6.85276923%2C9.9684299%20L11.5703186%2C3.14417309%20Z%22%2F%3E%3C%2Fg%3E%3C%2Fsvg%3E%0A");
         }
    </style>
    
    ${editorContent}
    `;
      }
    }
  }

  async setContent(content: string): Promise<void> {
    const result = await sntConfirm(
      sntTranslate(marker("Editor")),
      sntTranslate(
        marker(
          "Proseguendo il contenuto dell'editor verrà sovrascritto e perderei eventuali modifiche, confermi?"
        )
      ),
      {
        confirmText: sntTranslate(marker("Si")),
        cancelText: sntTranslate(marker("No"))
      }
    );

    if (result) {
      this.devxHtmlEditorComponent?.instance.option("value", content);
    }
  }

  onDevxContentReady(dxEvent: ContentReadyEvent): void {
    const quill = dxEvent.component.getQuillInstance();
    quill.uploader.preventImageUpload = true;

    quill.clipboard.addMatcher(Node.ELEMENT_NODE, (node: any, delta: any) => {
      delta.ops = delta.ops.map((op: any) => {
        return {
          insert: op.insert
        };
      });

      return delta;
    });
  }
}
